import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import countries from 'constants/countries';
import { VONAGE_ALLOWED_OFFICES, VONAGE_THALYS_ALLOWED_OFFICES } from 'constants/payment';
import ValidationForm from 'components/ui/ValidationForm';
import AddressInput from 'components/ui/AddressInput';
import { CurrencyInput } from 'components/ui/Inputs';
import Label from 'components/ui/Label';
import Select from 'components/ui/Select';
import config from 'utils/config';
import { isEmpty, isValidCountryCode, isValidEmail, isValidName, isValidPhoneNumberWithCountryCode } from 'utils/validators';
import { customerShape, legShape, passengersShape, productsShape } from 'constants/propTypes';

import { Errors } from 'components/ui/Payments/SplitPayment/SplitPayment.styles';

import SplitPayment from '../../SplitPayment';

import {
  BillingDetailsInput,
  MandatoryFieldLabel,
  CountryCodeInput,
  PhoneNumberInput,
  Field,
  ClearButton,
  CurrencyContainer
} from './CustomerNotPresentCardPayment.styles';

import AdyenCardPaymentPopup from './AdyenCardPaymentPopup';
import VonageCardPaymentPopup from './VonageCardPaymentPopup';

const isVonageEnabled = config('ENABLE_VONAGE', { canOverride: true }) === 'true';

const CustomerNotPresentCardPayment = ({
  reference,
  currency,
  currentAmount,
  isOnHold,
  isPackage,
  hasHotel,
  onAmountChange,
  totalPriceToBePaid,
  isPutOnHoldWithDeposit,
  customer,
  outbound,
  inbound,
  passengers,
  offices = [],
  userEmail,
  isGroup
}) => {
  const isVonageAvailable =
    isVonageEnabled &&
    offices.find((office) =>
      VONAGE_ALLOWED_OFFICES.concat(VONAGE_THALYS_ALLOWED_OFFICES).find((allowedOffice) => allowedOffice === office.name)
    );
  const isThalys = offices.find((office) => VONAGE_THALYS_ALLOWED_OFFICES.find((allowedOffice) => allowedOffice === office.name));
  const [isOpen, setIsOpen] = useState(false);
  const [isAddressSelected, setIsAddressSelected] = useState(Boolean(customer?.address));

  const initialFields = useMemo(
    () =>
      isAddressSelected
        ? {
            paymentAmount: currentAmount,
            firstName: customer.firstName || '',
            lastName: customer.lastName || '',
            addressLine1: customer.address?.street?.split(',')?.[0]?.trim() || '',
            addressLine2: customer.address?.street?.split(',')?.[1]?.trim() || '',
            addressLine3: '',
            city: customer.address?.city || '',
            postalCode: customer.address?.postalCode || '',
            countryCode: customer.address?.countryCode || 'GB',
            email: customer.email || '',
            phoneCountryCode: customer?.unformattedPhoneNumber?.countryCode?.replace('+', '') || '',
            stateOrProvince: customer?.address?.countryCode || '',
            phoneNumber: customer?.unformattedPhoneNumber?.number?.toString() || ''
          }
        : {
            paymentAmount: currentAmount,
            firstName: isGroup ? '' : customer?.firstName || '',
            lastName: isGroup ? '' : customer?.lastName || '',
            addressLine1: '',
            addressLine2: '',
            addressLine3: '',
            city: '',
            postalCode: '',
            countryCode: 'GB',
            email: isGroup ? '' : customer?.email || '',
            phoneCountryCode: isGroup ? '' : customer?.unformattedPhoneNumber?.countryCode?.replace('+', '') || '',
            stateOrProvince: '',
            phoneNumber: isGroup ? '' : customer?.unformattedPhoneNumber?.number?.toString() || ''
          },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  return (
    <ValidationForm
      validateInitialFields
      overrideFields={{
        paymentAmount: currentAmount
      }}
      validators={{
        paymentAmount: (values) => Number(values.paymentAmount) > 0 && Number(values.paymentAmount) <= totalPriceToBePaid,
        firstName: (values) => isValidName(values.firstName),
        lastName: (values) => isValidName(values.lastName),
        addressLine1: (values) => !isEmpty(values.addressLine1),
        city: (values) => !isEmpty(values.city),
        postalCode: (values) => !isEmpty(values.postalCode),
        countryCode: (values) => !isEmpty(values.countryCode),
        email: (values) => isValidEmail(values.email),
        phoneCountryCode: (values) => !isEmpty(values.phoneCountryCode) && isValidCountryCode(values.phoneCountryCode),
        stateOrProvince: (values) => (['US', 'CA'].includes(values.countryCode) ? !isEmpty(values.stateOrProvince) : true),
        phoneNumber: (values) => isValidPhoneNumberWithCountryCode(values.phoneNumber, values.phoneCountryCode)
      }}
      fields={initialFields}
    >
      {({ fields, onChange, invalidInputs, touchedInputs, hasInvalidInputs, resetField, changeField }) => {
        const invalidFields = Object.keys(invalidInputs)
          .filter((i) => touchedInputs[i])
          .reduce((acc, i) => {
            acc[i] = invalidInputs[i];

            return acc;
          }, {});

        const handleSubmit = (e) => {
          e.preventDefault();
          setIsOpen(true);
        };

        const handleChange = (event) => {
          onChange(event);

          if (onAmountChange) {
            onAmountChange(event.target.value);
          }
        };

        const handleAddressSelect = (address) => {
          if (address) {
            const {
              Company = '',
              SubBuilding = '',
              BuildingNumber = '',
              BuildingName = '',
              Street = '',
              SecondaryStreet = '',
              District = '',
              City = '',
              PostalCode = '',
              CountryIso2 = 'GB',
              ProvinceName = ''
            } = address;

            const addressLine1 = `${Company} ${SubBuilding} ${BuildingName} ${BuildingNumber}`.replace(/\s+/g, ' ').trim();
            const addressLine2 = `${Street} ${SecondaryStreet}`.replace(/\s+/g, ' ').trim();
            const district = District ? `${District} (${City})` : City;

            changeField('addressLine1')(addressLine1);
            changeField('addressLine2')(addressLine2);
            changeField('city')(district);
            changeField('postalCode')(PostalCode);
            changeField('countryCode')(CountryIso2);
            if (CountryIso2 === 'US' || CountryIso2 === 'CA') {
              changeField('stateOrProvince')(ProvinceName);
            }
          }

          setIsAddressSelected(true);
        };

        const handleClearDetails = () => {
          resetField('addressLine1');
          resetField('addressLine2');
          resetField('addressLine3');
          resetField('city');
          resetField('postalCode');
          resetField('countryCode');
          resetField('stateOrProvince');
          setIsAddressSelected(false);
        };

        const preventNonNumericValue = (e) => {
          const inputValue = e.target.value;
          const numericValue = inputValue.replace(/[^0-9]/g, '');

          onChange({ target: { name: e.target.name, value: numericValue } });
        };

        const blockInvalidChar = (e) => ['e', 'E', '+', '-'].includes(e.key) && e.preventDefault();

        return (
          <SplitPayment
            title={<FormattedMessage id="payments.AmountToPayByCard" />}
            label={<FormattedMessage id="payments.recordPayment" />}
            loading={false}
            disabled={hasInvalidInputs || !fields.paymentAmount}
            onSubmit={handleSubmit}
          >
            <CurrencyContainer>
              <CurrencyInput
                disabled={isPutOnHoldWithDeposit ? false : isOnHold}
                onChange={handleChange}
                value={currentAmount}
                name="paymentAmount"
                maxLength={9}
                currency={currency}
              />
              {isAddressSelected && (
                <ClearButton onClick={handleClearDetails}>
                  <FormattedMessage id="payments.clearDetails" />
                </ClearButton>
              )}
            </CurrencyContainer>
            <Errors>
              {invalidInputs.paymentAmount && Number(fields.paymentAmount) === 0 && <FormattedMessage id="payments.amountZero" />}
              {invalidInputs.paymentAmount && Number(fields.paymentAmount) !== 0 && <FormattedMessage id="payments.amountTooLarge" />}
            </Errors>
            <Field>
              <MandatoryFieldLabel htmlFor="firstName">
                <FormattedMessage id="billingDetailsForm.firstName" />
              </MandatoryFieldLabel>
              <BillingDetailsInput type="text" name="firstName" id="firstName" onChange={onChange} value={fields.firstName} />
            </Field>
            <Errors>{invalidFields.firstName && <FormattedMessage id="billingDetailsForm.incompleteField" />}</Errors>
            <Field>
              <MandatoryFieldLabel htmlFor="lastName">
                <FormattedMessage id="billingDetailsForm.lastName" />
              </MandatoryFieldLabel>
              <BillingDetailsInput type="text" name="lastName" id="lastName" value={fields.lastName} onChange={onChange} />
            </Field>
            <Errors>{invalidFields.lastName && <FormattedMessage id="billingDetailsForm.incompleteField" />}</Errors>
            {isAddressSelected ? (
              <>
                <Field>
                  <MandatoryFieldLabel htmlFor="addressLine1">
                    <FormattedMessage id="billingDetailsForm.addressLine1" />
                  </MandatoryFieldLabel>
                  <BillingDetailsInput
                    type="text"
                    name="addressLine1"
                    id="addressLine1"
                    value={fields.addressLine1}
                    onChange={onChange}
                    maxLength="49"
                  />
                </Field>
                <Errors>{invalidFields.addressLine1 && <FormattedMessage id="billingDetailsForm.incompleteField" />}</Errors>
                <Field>
                  <Label htmlFor="addressLine2">
                    <FormattedMessage id="billingDetailsForm.addressLine2" />
                  </Label>
                  <BillingDetailsInput
                    type="text"
                    name="addressLine2"
                    id="addressLine2"
                    value={fields.addressLine2}
                    onChange={onChange}
                    maxLength="49"
                  />
                </Field>
                <Errors>{invalidFields.addressLine2 && <FormattedMessage id="billingDetailsForm.incompleteField" />}</Errors>
                <Field>
                  <MandatoryFieldLabel htmlFor="city">
                    <FormattedMessage id="billingDetailsForm.city" />
                  </MandatoryFieldLabel>
                  <BillingDetailsInput type="text" name="city" id="city" value={fields.city} onChange={onChange} maxLength="49" />
                </Field>
                <Errors>{invalidFields.city && <FormattedMessage id="billingDetailsForm.incompleteField" />}</Errors>
                <Field>
                  <MandatoryFieldLabel htmlFor="postalCode">
                    <FormattedMessage id="billingDetailsForm.postcode" />
                  </MandatoryFieldLabel>
                  <BillingDetailsInput
                    type="text"
                    name="postalCode"
                    id="postalCode"
                    value={fields.postalCode}
                    onChange={onChange}
                    maxLength="15"
                  />
                </Field>
                <Errors>{invalidFields.postalCode && <FormattedMessage id="billingDetailsForm.incompleteField" />}</Errors>
                <Field>
                  <MandatoryFieldLabel htmlFor="countryCode">
                    <FormattedMessage id="billingDetailsForm.country" />
                  </MandatoryFieldLabel>
                  {/* TEMPORARY select field for country codes */}
                  <Select name="countryCode" id="countryCode" value={fields.countryCode} onChange={onChange}>
                    {countries.map(({ code, name }) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </Select>
                </Field>
                <Errors>{invalidFields.countryCode && <FormattedMessage id="billingDetailsForm.incompleteField" />}</Errors>
                {['US', 'CA'].includes(fields.countryCode) && (
                  <>
                    <Field>
                      <MandatoryFieldLabel htmlFor="stateOrProvince">
                        <FormattedMessage id="billingDetailsForm.stateOrProvince" />
                      </MandatoryFieldLabel>
                      <BillingDetailsInput
                        type="text"
                        name="stateOrProvince"
                        id="stateOrProvince"
                        value={fields.stateOrProvince}
                        onChange={onChange}
                      />
                    </Field>
                    <Errors>{invalidFields.stateOrProvince && <FormattedMessage id="billingDetailsForm.incompleteField" />}</Errors>
                  </>
                )}
              </>
            ) : (
              <Field>
                <MandatoryFieldLabel>
                  <FormattedMessage id="billingDetailsForm.billingAddress" />
                </MandatoryFieldLabel>
                <AddressInput onSelect={handleAddressSelect} />
              </Field>
            )}
            <Field>
              <MandatoryFieldLabel htmlFor="email">
                <FormattedMessage id="billingDetailsForm.billingEmail" />
              </MandatoryFieldLabel>
              <BillingDetailsInput type="text" name="email" id="email" value={fields.email} onChange={onChange} />
            </Field>
            <Errors>{invalidFields.email && <FormattedMessage id="billingDetailsForm.incompleteField" />}</Errors>
            <Field>
              <MandatoryFieldLabel htmlFor="phoneCountryCode">
                <FormattedMessage id="billingDetailsForm.billingPhoneNumber" />
              </MandatoryFieldLabel>
              <div>
                <CountryCodeInput
                  type="number"
                  max="9999"
                  name="phoneCountryCode"
                  id="phoneCountryCode"
                  value={fields.phoneCountryCode}
                  onChange={preventNonNumericValue}
                  onKeyDown={blockInvalidChar}
                />
                <PhoneNumberInput
                  type="number"
                  name="phoneNumber"
                  id="phoneNumber"
                  value={fields.phoneNumber}
                  onChange={preventNonNumericValue}
                  onKeyDown={blockInvalidChar}
                />
              </div>
            </Field>
            <Errors>
              {(invalidFields.phoneNumber || invalidFields.phoneCountryCode) && (
                <FormattedMessage id="billingDetailsForm.incompleteField" />
              )}
            </Errors>
            {isVonageAvailable ? (
              <VonageCardPaymentPopup
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                userEmail={userEmail}
                totalPriceToBePaid={totalPriceToBePaid}
                currency={currency}
                reference={reference}
                billingDetails={fields}
                paymentAmount={Number(fields.paymentAmount)}
                isPackage={isPackage}
                hasHotel={hasHotel}
                isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
                isSplitPayment
                outbound={outbound}
                inbound={inbound}
                passengers={passengers}
                isThalys={!!isThalys}
              />
            ) : (
              <AdyenCardPaymentPopup
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                totalPriceToBePaid={totalPriceToBePaid}
                currency={currency}
                reference={reference}
                isPutOnHoldWithDeposit={isPutOnHoldWithDeposit}
                billingDetails={fields}
                paymentAmount={Number(fields.paymentAmount)}
                isSplitPayment
                isPackage={isPackage}
                hasHotel={hasHotel}
                outbound={outbound}
                inbound={inbound}
                passengers={passengers}
                isGroup={isGroup}
              />
            )}
          </SplitPayment>
        );
      }}
    </ValidationForm>
  );
};

CustomerNotPresentCardPayment.propTypes = {
  currentAmount: PropTypes.string,
  isOnHold: PropTypes.bool,
  isPackage: PropTypes.bool,
  hasHotel: PropTypes.bool,
  onAmountChange: PropTypes.func,
  totalPriceToBePaid: PropTypes.number,
  reference: PropTypes.string,
  currency: PropTypes.string,
  isPutOnHoldWithDeposit: PropTypes.bool.isRequired,
  customer: customerShape,
  outbound: PropTypes.arrayOf(legShape),
  inbound: PropTypes.arrayOf(legShape),
  passengers: passengersShape,
  productFamilies: productsShape,
  offices: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string
    })
  ),
  userEmail: PropTypes.string,
  isGroup: PropTypes.bool
};

export default CustomerNotPresentCardPayment;
